import React from 'react'

import { useLayoutPanelsOptional } from '../../../../context/layoutPanels'

type PageRightRailContextValue = {
  isOpen: boolean
  open: () => void
  close: () => void
  toggle: () => void
}

const PageRightRailContext = React.createContext<PageRightRailContextValue | null>(null)

export function usePageRightRail(): PageRightRailContextValue {
  const ctx = React.useContext(PageRightRailContext)
  if (!ctx) {
    throw new Error('usePageRightRail must be used within PageRightRailProvider')
  }
  return ctx
}

type Props = {
  children: React.ReactNode
}

const PageRightRailProvider: React.FC<Props> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const layoutPanels = useLayoutPanelsOptional()

  React.useEffect(() => {
    document.querySelector('.page-content')?.classList.add('page-content--right-rail')
    return () => {
      document.querySelector('.page-content')?.classList.remove('page-content--right-rail')
    }
  }, [])

  const close = React.useCallback(() => setIsOpen(false), [])
  const open = React.useCallback(() => setIsOpen(true), [])
  const toggle = React.useCallback(() => setIsOpen((v) => !v), [])

  React.useEffect(() => {
    if (!layoutPanels) return
    return layoutPanels.registerRightRailClose(close)
  }, [layoutPanels, close])

  React.useEffect(() => {
    layoutPanels?.setRightRailIsOpen(isOpen)
  }, [isOpen, layoutPanels, layoutPanels?.setRightRailIsOpen])

  React.useEffect(() => {
    return () => {
      layoutPanels?.setRightRailIsOpen(false)
    }
  }, [layoutPanels, layoutPanels?.setRightRailIsOpen])

  React.useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove('right-rail-open')
      return
    }
    layoutPanels?.onRightRailOpened()
    document.body.classList.add('right-rail-open')
    return () => {
      document.body.classList.remove('right-rail-open')
    }
  }, [isOpen, layoutPanels, layoutPanels?.onRightRailOpened])

  const value = React.useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle]
  )

  return (
    <PageRightRailContext.Provider value={value}>
      {children}
    </PageRightRailContext.Provider>
  )
}

export default PageRightRailProvider
